// @ts-strict-ignore
import Progress from 'antd/lib/progress';
import Skeleton from 'antd/lib/skeleton';
import Space from 'antd/lib/space';
import Typography from 'antd/lib/typography';
import * as React from 'react';
import { iconAndAltForIntegrationType } from '../../helpers/integrationHelpers';
import { IntegrationType, PhasedRelease, SubscriptionFeatureType } from '../../types';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { ThunderboltFilled } from '@ant-design/icons';
import { isNil } from 'lodash';
import { PlanRestrictedFeatureWrapper } from '../planRestrictions/PlanRestrictedFeatureWrapper';
import { PausedByBlackoutBadge } from './PhasedReleaseCard';

export const MonitoringProgressCardsWrapper: React.FC<{ showBorder?: boolean }> = ({
    children,
    showBorder = false,
}) => {
    return (
        <div
            style={{
                backgroundColor: runwayColors.white,
                border: showBorder ? `1px solid ${antGrey.grey5}` : 'none',
                borderRadius: '8px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
            }}
        >
            {children}
        </div>
    );
};

export interface MonitoringProgressCardProps {
    title: string;
    integrationType?: IntegrationType;
    value: string;
    progress: number;
    isLoading?: boolean;
    strokeColor?: string;
    automatedByRunway?: boolean;
    phasedRelease: PhasedRelease | null;
    daysToPercent: number[] | null;
    disableLoadShimmer?: boolean;
    shouldApplyRestrictedTooltipForRollouts: boolean;
    isAutomatedPhasedRollout: boolean;
}

export const MonitoringProgressCard: React.FC<MonitoringProgressCardProps> = ({
    title,
    integrationType,
    value,
    progress,
    isLoading,
    strokeColor,
    automatedByRunway,
    disableLoadShimmer,
    phasedRelease,
    daysToPercent,
    shouldApplyRestrictedTooltipForRollouts,
    isAutomatedPhasedRollout,
}) => {
    const [icon, alt] = iconAndAltForIntegrationType(integrationType);

    const dayCount = daysToPercent?.length ?? 7;
    const currentDay = phasedRelease?.dayNumber ?? 0;

    const titleComponent = (
        <Typography.Text
            style={{
                fontSize: 12,
            }}
        >
            {title}
        </Typography.Text>
    );
    return (
        <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.Rollouts}>
            {(isDisabled) => (
                <div
                    style={{
                        position: 'relative',
                        minWidth: 200,
                    }}
                >
                    <Space
                        direction="vertical"
                        style={{
                            paddingTop: 14,
                            paddingBottom: 14,
                            paddingLeft: 18,
                            paddingRight: 16,
                            width: '100%',
                        }}
                        size={0}
                    >
                        {isLoading ?
                            <Skeleton
                                active={!disableLoadShimmer}
                                round={false}
                                paragraph={{
                                    rows: 2,
                                }}
                            />
                        :   <>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Space>
                                        {shouldApplyRestrictedTooltipForRollouts ?
                                            <PlanRestrictedFeatureWrapper
                                                feature={SubscriptionFeatureType.Rollouts}
                                                requiresTooltip
                                            >
                                                {titleComponent}
                                            </PlanRestrictedFeatureWrapper>
                                        :   titleComponent}
                                    </Space>
                                    {icon && <img src={icon} alt={alt} width={16} height={16} />}
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'baseline',
                                        flex: 1,
                                    }}
                                >
                                    <Space
                                        align="center"
                                        style={{
                                            marginTop: 10,
                                        }}
                                    >
                                        {automatedByRunway === true && (
                                            <ThunderboltFilled style={{ color: runwayColors.primary }} />
                                        )}
                                        <Typography.Text
                                            style={{
                                                fontSize: 30,
                                                lineHeight: '1em',
                                                color: antGrey.grey8,
                                            }}
                                        >
                                            {isDisabled ? '--' : value}
                                        </Typography.Text>
                                    </Space>
                                    {!!currentDay && !!dayCount && isAutomatedPhasedRollout && (
                                        <Space>
                                            {phasedRelease.isOnBlackoutPeriod && (
                                                <PausedByBlackoutBadge phasedRelease={phasedRelease} />
                                            )}
                                            <Typography.Text
                                                style={{
                                                    fontSize: 12,
                                                    color: antGrey.grey7,
                                                }}
                                            >
                                                {phasedRelease.isSuperseded ? 'Superseded on ' : ''}Day {currentDay} of{' '}
                                                {dayCount}
                                            </Typography.Text>
                                        </Space>
                                    )}
                                </div>
                                {!isNil(progress) && (
                                    <Progress
                                        className="bordered"
                                        percent={isDisabled ? 0 : progress}
                                        showInfo={false}
                                        strokeColor={strokeColor}
                                        style={{ width: '100%' }}
                                    />
                                )}
                            </>
                        }
                    </Space>
                </div>
            )}
        </PlanRestrictedFeatureWrapper>
    );
};
